// src/components/tickets/AddToWalletButton.js
import React, { useState, useEffect } from 'react';

// LocalStorage key for tracking added NFTs
const ADDED_NFTS_KEY = 'added_nfts_';

/**
 * AddToWalletButton - Universal button to add ERC-1155 NFT to any wallet
 *
 * Works with: MetaMask, Coinbase Wallet, Trust Wallet, Rainbow, and others
 *
 * @param {string} contractAddress - The EventTicket contract address
 * @param {string} tokenId - The full token ID (eventId << 128 | itemId)
 * @param {string} imageUrl - Optional image URL for the NFT
 * @param {boolean} hideWhenAdded - If true, completely hide the button when NFT is already added
 */
export default function AddToWalletButton({ contractAddress, tokenId, imageUrl, hideWhenAdded = false }) {
  const [status, setStatus] = useState('idle'); // idle, adding, success, added, error
  const [errorMessage, setErrorMessage] = useState('');
  const [walletType, setWalletType] = useState('');

  // Check if NFT was previously added
  useEffect(() => {
    const checkIfAdded = () => {
      if (!window.ethereum?.selectedAddress || !tokenId) return;

      try {
        const account = window.ethereum.selectedAddress.toLowerCase();
        const storageKey = ADDED_NFTS_KEY + account;
        const addedNFTs = JSON.parse(localStorage.getItem(storageKey) || '[]');

        if (addedNFTs.includes(tokenId)) {
          setStatus('added');
        }
      } catch (error) {
        console.error('Error checking added NFTs:', error);
      }
    };

    checkIfAdded();
  }, [tokenId]);

  // Mark NFT as added in localStorage
  const markAsAdded = () => {
    if (!window.ethereum?.selectedAddress || !tokenId) return;

    try {
      const account = window.ethereum.selectedAddress.toLowerCase();
      const storageKey = ADDED_NFTS_KEY + account;
      const addedNFTs = JSON.parse(localStorage.getItem(storageKey) || '[]');

      if (!addedNFTs.includes(tokenId)) {
        addedNFTs.push(tokenId);
        localStorage.setItem(storageKey, JSON.stringify(addedNFTs));
      }
    } catch (error) {
      console.error('Error saving added NFT:', error);
    }
  };

  // Detect wallet type
  const detectWallet = () => {
    if (window.ethereum) {
      if (window.ethereum.isMetaMask) return 'MetaMask';
      if (window.ethereum.isCoinbaseWallet) return 'Coinbase Wallet';
      if (window.ethereum.isTrust) return 'Trust Wallet';
      if (window.ethereum.isRainbow) return 'Rainbow';
      return 'Web3 Wallet';
    }
    return null;
  };

  const handleAddToWallet = async () => {
    const wallet = detectWallet();

    if (!wallet) {
      setStatus('error');
      setErrorMessage('No Web3 wallet detected. Please install MetaMask, Coinbase Wallet, or another Web3 wallet.');
      return;
    }

    setWalletType(wallet);

    try {
      setStatus('adding');
      setErrorMessage('');

      // Try ERC-1155 first (newer wallets)
      try {
        const wasAdded = await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC1155',
            options: {
              address: contractAddress,
              tokenId: tokenId,
              image: imageUrl,
            },
          },
        });

        if (wasAdded) {
          setStatus('success');
          markAsAdded();
          setTimeout(() => setStatus('added'), 2000);
          return;
        } else {
          throw new Error('User rejected');
        }
      } catch (erc1155Error) {
        // If ERC1155 fails, try ERC721 (some wallets treat 1155 as 721)
        try {
          const wasAdded = await window.ethereum.request({
            method: 'wallet_watchAsset',
            params: {
              type: 'ERC721',
              options: {
                address: contractAddress,
                tokenId: tokenId,
              },
            },
          });

          if (wasAdded) {
            setStatus('success');
            markAsAdded();
            setTimeout(() => setStatus('added'), 2000);
            return;
          }
        } catch (erc721Error) {
          // Both failed, show manual instructions
          throw erc1155Error;
        }
      }
    } catch (error) {
      console.error('Error adding NFT to wallet:', error);
      setStatus('error');

      if (error.message?.includes('rejected') || error.code === 4001) {
        setErrorMessage(`You cancelled the request in ${wallet}.`);
      } else if (error.code === -32601 || error.message?.includes('not supported')) {
        setErrorMessage(`${wallet} does not support adding ERC-1155 NFTs automatically. See manual instructions below.`);
      } else {
        setErrorMessage(error.message || 'Failed to add NFT. See manual instructions below.');
      }
    }
  };

  const getButtonText = () => {
    switch (status) {
      case 'adding':
        return 'Adding...';
      case 'success':
        return '✓ Added Successfully';
      case 'added':
        return '✓ In Wallet';
      case 'error':
        return 'Add Manually';
      default:
        return '+ Add to Wallet';
    }
  };

  const getButtonClass = () => {
    let baseClass = 'add-to-wallet-button';
    if (status === 'success' || status === 'added') baseClass += ' success';
    if (status === 'error') baseClass += ' error';
    if (status === 'adding') baseClass += ' loading';
    return baseClass;
  };

  const handleButtonClick = () => {
    if (status === 'added') {
      // Already added - open wallet or show options
      openWalletView();
    } else {
      // Not added yet - add to wallet
      handleAddToWallet();
    }
  };

  const openWalletView = () => {
    const wallet = detectWallet();

    if (wallet === 'MetaMask') {
      // MetaMask deep link to NFTs tab
      window.open('https://metamask.app.link/nfts', '_blank');
    } else {
      // Fallback: open block explorer
      const explorerUrl = getBlockExplorerLink();
      window.open(explorerUrl, '_blank');
    }
  };

  const getBlockExplorerLink = () => {
    // Determine network and block explorer
    const chainId = window.ethereum?.chainId;

    // Base Sepolia = 0x14a34 (84532)
    if (chainId === '0x14a34' || chainId === 84532) {
      return `https://sepolia.basescan.org/token/${contractAddress}?a=${window.ethereum?.selectedAddress}`;
    }
    // Base Mainnet = 0x2105 (8453)
    if (chainId === '0x2105' || chainId === 8453) {
      return `https://basescan.org/token/${contractAddress}?a=${window.ethereum?.selectedAddress}`;
    }
    // Fallback
    return `https://sepolia.basescan.org/token/${contractAddress}`;
  };

  // If hideWhenAdded is true and NFT is already added, don't render anything
  if (hideWhenAdded && status === 'added') {
    return null;
  }

  return (
    <div className="add-to-wallet-container">
      <button
        className={getButtonClass()}
        onClick={handleButtonClick}
        disabled={status === 'adding' || status === 'success'}
        title={status === 'added' ? 'Click to view in wallet' : 'Add this NFT to your wallet'}
      >
        {getButtonText()}
      </button>

      {status === 'added' && (
        <p className="added-hint">
          Click to view in your wallet or on{' '}
          <a
            href={getBlockExplorerLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="explorer-link-small"
          >
            BaseScan →
          </a>
        </p>
      )}

      {status === 'error' && (
        <div className="add-to-wallet-error">
          <p className="error-msg">{errorMessage}</p>

          <details className="manual-instructions" open>
            <summary>Manual Instructions for {walletType || 'Your Wallet'}</summary>
            <div className="instructions-content">

              <div className="method-tabs">
                <h4>Option 1: View on Block Explorer</h4>
                <p>The easiest way to view your NFT:</p>
                <a
                  href={getBlockExplorerLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="explorer-link"
                >
                  View on BaseScan →
                </a>
              </div>

              <div className="method-tabs">
                <h4>Option 2: Add to {walletType || 'MetaMask'} Manually</h4>
                <ol>
                  <li>Open your wallet app</li>
                  <li>Go to <strong>NFTs</strong> tab</li>
                  <li>Scroll down and click <strong>"Import NFT"</strong> or <strong>"+"</strong></li>
                  <li>Enter the details below:</li>
                </ol>

                <div className="copy-fields">
                  <div className="input-group">
                    <label>Contract Address:</label>
                    <div className="input-with-copy">
                      <input
                        type="text"
                        value={contractAddress}
                        readOnly
                        onClick={(e) => {
                          e.target.select();
                          navigator.clipboard.writeText(contractAddress);
                        }}
                      />
                      <button
                        className="copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(contractAddress);
                          alert('✓ Copied!');
                        }}
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="input-group">
                    <label>Token ID:</label>
                    <div className="input-with-copy">
                      <input
                        type="text"
                        value={tokenId}
                        readOnly
                        onClick={(e) => {
                          e.target.select();
                          navigator.clipboard.writeText(tokenId);
                        }}
                      />
                      <button
                        className="copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(tokenId);
                          alert('✓ Copied!');
                        }}
                      >
                        📋
                      </button>
                    </div>
                  </div>
                </div>

                <ol start="5">
                  <li>Click <strong>"Add"</strong> or <strong>"Import"</strong></li>
                </ol>
              </div>

              <div className="method-tabs">
                <h4>Option 3: Use OpenSea (If Available)</h4>
                <p>View and manage your NFT on OpenSea:</p>
                <a
                  href={`https://testnets.opensea.io/assets/base-sepolia/${contractAddress}/${tokenId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="explorer-link"
                >
                  View on OpenSea Testnet →
                </a>
              </div>

              <p className="support-note">
                <strong>💡 Note:</strong> Different wallets have varying support for ERC-1155 NFTs.
                If your wallet doesn't support importing NFTs, you can always view them on the block explorer.
              </p>
            </div>
          </details>
        </div>
      )}

      <style>{`
        .add-to-wallet-container {
          margin-top: 15px;
        }

        .add-to-wallet-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .add-to-wallet-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.5);
        }

        .add-to-wallet-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .add-to-wallet-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .add-to-wallet-button.success {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        }

        .add-to-wallet-button.error {
          background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
        }

        .add-to-wallet-button.loading {
          background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
        }

        .added-hint {
          margin: 8px 0 0 0;
          padding: 8px 12px;
          background: rgba(17, 153, 142, 0.1);
          border-radius: 6px;
          font-size: 12px;
          color: #38ef7d;
          text-align: center;
        }

        .explorer-link-small {
          color: #38ef7d;
          text-decoration: underline;
          font-weight: 600;
        }

        .explorer-link-small:hover {
          color: #11998e;
        }

        .add-to-wallet-error {
          margin-top: 15px;
          padding: 15px;
          background: rgba(235, 51, 73, 0.08);
          border-left: 4px solid #eb3349;
          border-radius: 8px;
        }

        .error-msg {
          margin: 0 0 12px 0;
          color: #eb3349;
          font-weight: 500;
          font-size: 13px;
        }

        .manual-instructions {
          margin-top: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.2);
        }

        .manual-instructions summary {
          cursor: pointer;
          font-weight: 600;
          color: #667eea;
          padding: 12px 15px;
          user-select: none;
          list-style: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .manual-instructions summary::-webkit-details-marker {
          display: none;
        }

        .manual-instructions summary:after {
          content: '▼';
          font-size: 12px;
          transition: transform 0.3s;
        }

        .manual-instructions[open] summary:after {
          transform: rotate(180deg);
        }

        .manual-instructions summary:hover {
          background: rgba(102, 126, 234, 0.1);
        }

        .instructions-content {
          padding: 15px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .method-tabs {
          margin-bottom: 20px;
          padding: 15px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .method-tabs h4 {
          margin: 0 0 10px 0;
          color: #667eea;
          font-size: 14px;
        }

        .method-tabs p {
          margin: 5px 0 10px 0;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
        }

        .method-tabs ol {
          margin: 10px 0;
          padding-left: 20px;
        }

        .method-tabs li {
          margin-bottom: 8px;
          line-height: 1.6;
          font-size: 13px;
        }

        .explorer-link {
          display: inline-block;
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-decoration: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s;
        }

        .explorer-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .copy-fields {
          margin: 15px 0;
        }

        .input-group {
          margin-bottom: 15px;
        }

        .input-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .input-with-copy {
          display: flex;
          gap: 8px;
        }

        .input-with-copy input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          background: rgba(0, 0, 0, 0.4);
          color: white;
          border-radius: 6px;
          font-family: 'Courier New', monospace;
          font-size: 11px;
          transition: all 0.2s;
        }

        .input-with-copy input:focus {
          outline: none;
          border-color: #667eea;
          background: rgba(0, 0, 0, 0.5);
        }

        .input-with-copy input:hover {
          border-color: rgba(255, 255, 255, 0.25);
        }

        .copy-btn {
          padding: 10px 16px;
          background: rgba(102, 126, 234, 0.2);
          color: white;
          border: 1px solid rgba(102, 126, 234, 0.3);
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .copy-btn:hover {
          background: rgba(102, 126, 234, 0.3);
          border-color: #667eea;
        }

        .copy-btn:active {
          transform: scale(0.95);
        }

        .support-note {
          margin-top: 15px;
          padding: 12px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.5;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 6px;
          border-left: 3px solid #667eea;
        }

        .support-note strong {
          color: #667eea;
        }
      `}</style>
    </div>
  );
}
